import { useState, useEffect } from 'react';
import './CheckoutStep.css';
import { Logo } from './Logo';
import type { PlanTier } from './PricingStep';
import { getIcon } from './Icons';
import { sendOrderEmail } from '../lib/email';
import { COUNTRIES } from '../lib/geoLocation';
import { INDIA_CONFIG } from '../lib/pricingConfig';
import './CheckoutStep.css';

// Declare Razorpay on window object
declare global {
    interface Window {
        Razorpay: any;
    }
}

function loadScript(src: string) {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
}

export interface MemberData {
    firstName: string;
    lastName: string;
    phone: string;
    dob: string;
    address: string;
    zipCode: string;
    amountPaid: number;
}

interface CheckoutStepProps {
    email: string;
    plan: PlanTier;
    onBack: () => void;
    onChangeTier: () => void;
    onSubmit: (paymentId: string, memberData: MemberData) => void;
    countryCode?: string;
    isInternational?: boolean;
    onCountryChange?: (countryCode: string) => void;
}

const x1Markers = [
    {
        category: "Complete Blood Count (CBC)",
        items: [
            "Hemoglobin", "RBC Count", "PCV / Hematocrit", "MCV", "MCH", "MCHC", "RDW-CV", "RDW-SD",
            "Total Leukocyte Count (TLC)", "Neutrophils %", "Lymphocytes %", "Monocytes %", "Eosinophils %", "Basophils %",
            "Absolute Counts (Neutrophil, Lymphocyte, Monocyte, Eosinophil, Basophil)",
            "Platelet Count", "MPV", "PCT", "PDW", "P-LCR", "P-LCC", "Mentzer Index"
        ]
    },
    {
        category: "Diabetes & Inflammation",
        items: [
            "Fasting Plasma Glucose", "HbA1c", "Estimated Average Glucose (eAG)",
            "High-Sensitivity CRP (hs-CRP)"
        ]
    },
    {
        category: "Liver Function",
        items: [
            "Total Bilirubin", "Direct & Indirect Bilirubin", "SGOT (AST)", "SGPT (ALT)", "AST/ALT Ratio",
            "Alkaline Phosphatase", "Albumin", "Total Protein", "GGT", "Globulin"
        ]
    },
    {
        category: "Kidney & Electrolytes",
        items: [
            "Blood Urea", "Creatinine", "BUN", "eGFR (CKD-EPI)", "Uric Acid", "BUN/Creatinine",
            "Calcium", "Phosphorus", "Sodium", "Potassium", "Chloride"
        ]
    },
    {
        category: "Lipid Profile & Heart Health",
        items: [
            "Total Cholesterol", "Triglycerides", "HDL", "LDL", "VLDL", "Non-HDL",
            "Cholesterol Ratios", "Apolipoprotein B (ApoB)", "Lipoprotein(a)"
        ]
    },
    {
        category: "Iron Studies",
        items: [
            "Serum Iron", "TIBC", "UIBC", "Transferrin Saturation %"
        ]
    },
    {
        category: "Vitamins",
        items: [
            "Vitamin B12", "Vitamin D (25-OH)"
        ]
    },
    {
        category: "Thyroid Function",
        items: [
            "TSH (3rd Gen)", "Free T3", "Free T4"
        ]
    },
    {
        category: "Hormones & Cancer",
        items: [
            "LH", "FSH", "Total Testosterone", "Free Testosterone", "PSA (Total)", "CA-125"
        ]
    }
];

const r1Markers = [
    {
        category: "Complete Blood Count (CBC)",
        description: "Evaluates overall health, detecting infections, anemia, and blood disorders.",
        items: [
            "Hemoglobin Hb", "RBC Count", "PCV Haematocrit", "MCV", "MCH", "MCHC",
            "RDW-CV", "RDW (Red Cell Distribution Width)", "Mentzer Index",
            "WBC-Total Counts Leucocytes", "Neutrophils", "Lymphocytes", "Monocytes", "Eosinophils", "Basophils",
            "Absolute Neutrophil Count", "Absolute Lymphocyte Count", "Absolute Monocyte Count", "Absolute Eosinophil Count", "Absolute Basophils Count",
            "Platelet Count Thrombocyte count", "MPV Mean Platelet Volume", "PCT", "PDW Platelet Distribution Width", "P-LCC", "P-LCR"
        ]
    },
    {
        category: "Glucose & Diabetes",
        description: "Measures blood sugar levels to diagnose diabetes and monitor control.",
        items: [
            "Glucose Fasting", "HbA1c (Glycosylated Haemoglobin)", "Average Blood Glucose"
        ]
    },
    {
        category: "Kidney Function Test (KFT)",
        description: "Assesses kidney filtration and mineral balance.",
        items: [
            "Blood Urea", "BUN", "Creatinine", "Uric Acid", "eGFR",
            "BUN/Creatinine Ratio", "Urea/Creatinine Ratio",
            "Calcium", "Phosphorus", "Sodium", "Potassium", "Chloride"
        ]
    },
    {
        category: "Liver Function Test (LFT)",
        description: "Assesses liver health, enzymes, and proteins.",
        items: [
            "Bilirubin Total", "Bilirubin Direct", "Bilirubin Indirect",
            "SGOT/AST", "SGPT/ALT", "SGOT/SGPT Ratio", "Alkaline Phosphatase", "GGTP",
            "Total Protein", "Albumin", "Globulin", "A/G Ratio"
        ]
    },
    {
        category: "Lipid Profile",
        description: "Assesses cholesterol and heart health risks.",
        items: [
            "Total Cholesterol", "Triglycerides", "HDL Cholesterol Direct", "LDL Cholesterol", "VLDL", "Non-HDL Cholesterol",
            "CHOL/HDL Ratio", "HDL/LDL Ratio", "LDL/HDL Ratio"
        ]
    },
    {
        category: "Thyroid Profile Total",
        description: "Evaluates thyroid gland function and metabolism.",
        items: [
            "T3 Total", "T4 Total", "TSH Ultra-sensitive"
        ]
    },
    {
        category: "Urine Routine & Microscopic",
        description: "Evaluates kidney and urinary tract health.",
        items: [
            "Color", "Transparency", "Specific Gravity", "pH", "Volume",
            "Sugar", "Albumin", "Bilirubin", "Urobilinogen", "Ketones", "Nitrate", "Leucocyte Esterase",
            "Pus Cells", "Red Blood Cells", "Epithelial Cells", "Casts", "Crystals", "Bacteria", "Yeast Cells", "Protozoa"
        ]
    },
    {
        category: "Inflammation (ESR)",
        description: "Indicates inflammation in the body.",
        items: ["Erythrocyte Sedimentation Rate (ESR)"]
    }
];

export function CheckoutStep({ email, plan, onBack, onChangeTier, onSubmit, countryCode = 'IN', isInternational = false, onCountryChange }: CheckoutStepProps) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        zipCode: '',
        dobMonth: '',
        dobDay: '',
        dobYear: '',
        phone: '',
        country: countryCode,
        agreeTerms: false,
        agreeMarketing: false,
        paymentMethod: 'card' as 'card' | 'bank',
        cardNumber: '',
        expMonth: '',
        expYear: '',
        cvv: '',
    });

    const [promoCode, setPromoCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [quantity, setQuantity] = useState(1); // Default is 1 membership
    const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [showSnowfall, setShowSnowfall] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [activePromoCode, setActivePromoCode] = useState('');

    // Blood Panel Selection State
    // Default to R1 for Baseline (to match 999 price), X1 for others
    const defaultPanel = plan.id === 'baseline' ? 'r1' : 'x1';
    const [panelOption, setPanelOption] = useState<'r1' | 'x1' | 'own'>(defaultPanel);
    const [showLabVerification, setShowLabVerification] = useState(false);
    const [labReportApproved, setLabReportApproved] = useState(false);
    const [labReportDiscount, setLabReportDiscount] = useState(0);

    // Panel Expansion State
    const [expandedPanel, setExpandedPanel] = useState<'r1' | 'x1' | null>(null);

    const togglePanelExpansion = (panel: 'r1' | 'x1', e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedPanel(expandedPanel === panel ? null : panel);
    };

    // Lab Report Verification State
    const [reportDate, setReportDate] = useState('');
    const [parameterCount, setParameterCount] = useState('');
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [labVerificationMessage, setLabVerificationMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Add-ons State
    const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);

    // Available Add-ons
    const addOns = [
        {
            id: 'gut-microbiome',
            name: 'Advanced Gut Microbiome Health Test',
            description: 'Understand the connection between the food you eat and its effect on your health and well-being.',
            poweredBy: 'Decode Age',
            price: 10800,
            image: '/gut-microbiome.png',
        },
    ];

    // Calculate add-ons total
    const addOnsTotal = addOns
        .filter(addon => selectedAddOns.includes(addon.id))
        .reduce((sum, addon) => sum + addon.price, 0);

    const handleAddOnToggle = (addonId: string) => {
        setSelectedAddOns(prev =>
            prev.includes(addonId)
                ? prev.filter(id => id !== addonId)
                : [...prev, addonId]
        );
    };

    // All health domains covered by Nostavia
    const healthDomains = [
        { id: 'heart', name: 'Heart Health', required: true },
        { id: 'thyroid', name: 'Thyroid Health', required: true },
        { id: 'immune', name: 'Immune Regulation', required: false },
        { id: 'hormone', name: 'Hormone Health', required: false },
        { id: 'metabolic', name: 'Metabolic Health', required: true },
        { id: 'liver', name: 'Liver Health', required: true },
        { id: 'kidney', name: 'Kidney Health', required: true },
        { id: 'nutrients', name: 'Nutrients', required: false },
        { id: 'blood', name: 'Blood Health', required: false },
        { id: 'inflammation', name: 'Inflammation', required: false },
    ];


    const handleRemovePromo = () => {
        setDiscount(0);
        setPromoCode('');
        setPromoMessage(null);
        setQuantity(1);
        setShowSnowfall(false);
        setShowConfetti(false);
        setActivePromoCode('');
    };

    // Lab Report Verification Handlers
    const handlePanelOptionChange = (option: 'r1' | 'x1' | 'own') => {
        setPanelOption(option);
        if (option === 'own') {
            setShowLabVerification(true);
            // Dynamic discount based on plan for "Own Lab Report"
            // Baseline: 700 (Total 299), Pro/Ultimate: 2500
            const discountAmount = plan.id === 'baseline' ? 700 : 2500;
            setLabReportDiscount(discountAmount);
        } else {
            // Reset lab report state when switching to Nostavia Panels
            setShowLabVerification(false);
            setLabReportApproved(false);
            setLabReportDiscount(0);
            setReportDate('');
            setParameterCount('');
            setSelectedDomains([]);
            setLabVerificationMessage(null);
        }
    };

    const handleDomainToggle = (domainId: string) => {
        setSelectedDomains(prev =>
            prev.includes(domainId)
                ? prev.filter(d => d !== domainId)
                : [...prev, domainId]
        );
    };

    const isReportWithinTwoMonths = (dateStr: string): boolean => {
        if (!dateStr) return false;
        const reportDateObj = new Date(dateStr);
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        return reportDateObj >= twoMonthsAgo;
    };

    const handleVerifyLabReport = () => {
        setLabVerificationMessage(null);

        // Check if report is within 2 months
        if (!isReportWithinTwoMonths(reportDate)) {
            setLabVerificationMessage({
                type: 'error',
                text: 'Lab report must be from within the last 2 months.'
            });
            setLabReportApproved(false);
            // Revert to base discount for "Own" (999) instead of 0
            setLabReportDiscount(999);
            return;
        }

        const numParams = parseInt(parameterCount, 10) || 0;
        const requiredDomains = ['heart', 'metabolic', 'liver', 'kidney', 'thyroid'];
        const hasAllRequired = requiredDomains.every(d => selectedDomains.includes(d));
        const hasMinDomains = selectedDomains.length >= 6;
        const hasMinParams = numParams >= 70;

        // Approval logic: (70+ params) OR (6+ domains including all required)
        const isApproved = hasMinParams || (hasMinDomains && hasAllRequired);

        if (isApproved) {
            setLabReportApproved(true);
            const discountAmount = plan.id === 'baseline' ? 700 : 2500;
            setLabReportDiscount(discountAmount);
            setLabVerificationMessage({
                type: 'success',
                text: `✓ Lab report approved! ₹${discountAmount.toLocaleString('en-IN')} discount applied.`
            });
        } else {
            setLabReportApproved(false);
            // Revert to base discount for "Own"
            const discountAmount = plan.id === 'baseline' ? 700 : 2500;
            setLabReportDiscount(discountAmount);
            let reason = 'Your report needs either 70+ parameters, or 6+ domains including Heart, Metabolic, Liver, Kidney & Thyroid Health.';
            setLabVerificationMessage({ type: 'error', text: reason });
        }
    };

    const handleResetLabVerification = () => {
        setLabReportApproved(false);
        const discountAmount = plan.id === 'baseline' ? 700 : 2500;
        setLabReportDiscount(discountAmount); // Reset to base discount for "Own"
        setReportDate('');
        setParameterCount('');
        setSelectedDomains([]);
        setLabVerificationMessage(null);
    };

    const handleApplyPromo = () => {
        setPromoMessage(null);
        setDiscount(0);
        setQuantity(1); // Reset quantity by default

        const code = promoCode.trim().toUpperCase();
        if (!code) return;

        if (code === 'FAMILYPLUS') {
            setQuantity(2); // 2 Memberships
            const totalBasePrice = plan.price * 2;
            const discountAmount = Math.round(totalBasePrice * 0.20);
            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `Family Plus applied! 2 Memberships with 20% off. You saved ₹${discountAmount.toLocaleString('en-IN')}` });
        } else if (code === 'SAVE30') {
            const basePrice = isInternational ? getDisplayPrice(plan.id) : plan.price;
            const discountAmount = Math.round(basePrice * 0.30);
            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `Code applied! You saved ${currencySymbol}${discountAmount.toLocaleString(isInternational ? 'en-US' : 'en-IN')}` });
        } else if (code === 'WELCOME20') {
            const basePrice = isInternational ? getDisplayPrice(plan.id) : plan.price;
            const discountAmount = Math.round(basePrice * 0.20);
            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `Welcome offer applied! You saved ${currencySymbol}${discountAmount.toLocaleString(isInternational ? 'en-US' : 'en-IN')}` });
        } else if (code === 'SANTA') {
            const basePrice = isInternational ? getDisplayPrice(plan.id) : plan.price;
            const discountAmount = Math.round(basePrice * 0.30);
            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `🎅 Ho Ho Ho! 30% off applied! You saved ${currencySymbol}${discountAmount.toLocaleString(isInternational ? 'en-US' : 'en-IN')}` });
            setShowSnowfall(true);
            setActivePromoCode('SANTA');
        } else if (code === 'NEWYEAR2026') {
            if (plan.id === 'baseline') {
                setPromoMessage({ type: 'error', text: 'This code is only valid for Pro and Ultimate plans.' });
                return;
            }

            // New Year Promo Logic:
            // Pro/Ultimate -> ₹4000
            // If "Own Lab Report" is selected -> ₹3000 (handled in useEffect/recalc)

            // We set the base discount here to reach 4000. 
            // If it's Pro (5999) -> 4000 (Discount 1999)
            // If it's Ultimate (7499) -> 4000 (Discount 3499)

            const targetPrice = 4000;
            const currentPrice = plan.price;
            const discountAmount = Math.max(0, currentPrice - targetPrice);

            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `🎉 Happy New Year! Price dropped to ${currencySymbol}4,000!` });
            setShowConfetti(true);
            setActivePromoCode('NEWYEAR2026');
        } else if (code === 'FREECOMPLETELY') {
            const basePrice = isInternational ? getDisplayPrice(plan.id) : plan.price;
            setDiscount(basePrice);
            setPromoMessage({ type: 'success', text: '100% Discount applied!' });
        } else if (code === 'NV_VIP_97_XQ2') {
            const basePrice = isInternational ? getDisplayPrice(plan.id) : plan.price;
            const discountAmount = Math.round(basePrice * 0.97);
            setDiscount(discountAmount);
            setPromoMessage({ type: 'success', text: `VIP Secret Unlocked! 97% off applied. You saved ${currencySymbol}${discountAmount.toLocaleString(isInternational ? 'en-US' : 'en-IN')}` });
        } else {
            setPromoMessage({ type: 'error', text: 'Invalid promo code' });
        };
    };

    // Effect to handle dynamic pricing adjustments for NEWYEAR2026 code
    // Specifically when switching between "Nostavia Panel" and "Own Lab Report"
    useEffect(() => {
        if (activePromoCode === 'NEWYEAR2026') {
            if (plan.id === 'baseline') return; // Should not happen due to check in handleApplyPromo but safe to keep

            let targetPrice = 4000;

            // If user has selected "Own Lab Report" (panelOption === 'own')
            // The requirement is: "if this code is applied and the user chooses custom lab report insted of nostavia panel, the price will be ₹3000"
            // We don't strictly require "labReportApproved" based on the prompt "user chooses custom lab report", 
            // but usually "Own Lab Report" implies verification for discount. 
            // However, the standard "Own Lab Report" gives 2500 off ONLY if approved.
            // The prompt implies a flat price of 3000 if "custom lab report" is chosen.
            // Let's assume selecting the option is enough to trigger the potential price, 
            // OR strictly follow the "labReportApproved" logic if we want to be safe.
            // Given "price will be ₹3000", let's make it so that the final price lands at 3000.

            // Standard logic: 
            // - Nostavia Panel: Plan Price - Discount = 4000
            // - Own Panel: Plan Price - Discount - LabDiscount(2500) = ...?
            // This standard logic makes it complex because LabDiscount is variable (0 or 2500).

            // To achieve exact ₹3000 for Own Lab Report:
            // We should override the standard calculation logic or adjust `discount` dynamically.

            if (panelOption === 'own' && labReportApproved) {
                targetPrice = 3000;
                // But wait, the standard "checkout" logic subtracts `labReportDiscount` (2500) separately.
                // finalPrice = totalPrice - totalDiscount
                // totalDiscount = discount + labReportDiscount
                // We want finalPrice = 3000.
                // 3000 = plan.price - (discount + 2500)
                // discount = plan.price - 3000 - 2500
                // If plan is 5999: discount = 5999 - 5500 = 499
                // If plan is 7499: discount = 7499 - 5500 = 1999

                const requiredDiscount = Math.max(0, plan.price - targetPrice - labReportDiscount);
                setDiscount(requiredDiscount);
                setPromoMessage({ type: 'success', text: `🎉 Happy New Year! Price dropped to ${currencySymbol}3,000 with Lab Report!` });

            } else if (panelOption === 'own' && !labReportApproved) {
                // If they selected "Own" but it's not approved yet, what is the price?
                // Usually it stays at the base price until approved.
                // Or does "chooses custom lab report" mean just clicking the box?
                // The prompt says "user chooses custom lab report".
                // But without approval, they haven't really "provided" a report.
                // Let's stick to the behavior that they need to verify to get the "lab benefit".
                // So if not approved, it acts like the standard coupon (4000).

                // However, there is a nuance: if they just click "Use My Own Lab Report", 
                // they might expect to see 3000 immediately? 
                // BUT, in standard flow, selecting 'own' doesn't give a discount until verified.
                // So valid interpretation: "Price becomes 3000 [when valid custom report is used]".
                // I will stick to the 4000 price if not approved yet to incentivize approval.

                const targetPriceUnverified = 4000;
                if (plan.price > targetPriceUnverified) {
                    setDiscount(plan.price - targetPriceUnverified);
                    setPromoMessage({ type: 'success', text: `🎉 Happy New Year! Price dropped to ${currencySymbol}4,000!` });
                }

            } else {
                // Nostavia Panel
                const targetPrice = 4000;
                if (plan.price > targetPrice) {
                    setDiscount(plan.price - targetPrice);
                    setPromoMessage({ type: 'success', text: `🎉 Happy New Year! Price dropped to ${currencySymbol}4,000!` });
                }
            }
        }
    }, [panelOption, labReportApproved, activePromoCode, plan.price, labReportDiscount]);

    // Currency and price helpers for international
    const currencySymbol = isInternational ? '$' : '₹';
    const getDisplayPrice = (planId: 'baseline' | 'performance' | 'complete'): number => {
        if (isInternational) {
            const usdPrices = { baseline: 59, performance: 99, complete: 79 };
            return usdPrices[planId];
        }
        return plan.price;
    };
    const displayPrice = getDisplayPrice(plan.id);

    // Calculate Discount for R1 Panel
    // Base prices for Pro/Ultimate include X1. R1 reduces price by 2000.
    // Baseline already prices in R1, so no discount needed.
    const panelDiscount = (panelOption === 'r1' && plan.id !== 'baseline' && !isInternational) ? 2000 : 0;

    // Calculate Upcharge for X1 Panel on Baseline
    // Baseline price (999) includes R1. Selecting X1 adds 2000.
    const panelUpcharge = (panelOption === 'x1' && plan.id === 'baseline' && !isInternational) ? 2000 : 0;

    const totalPrice = (isInternational ? displayPrice : plan.price) * quantity + (isInternational ? 0 : addOnsTotal) + panelUpcharge;
    const totalDiscount = discount + (isInternational ? 0 : labReportDiscount) + panelDiscount;
    const finalPrice = Math.max(0, totalPrice - totalDiscount);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // Mouse tracking for 3D tilt and shine effect (copied from PricingStep)
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        // Calculate rotation (max 10 degrees for smaller card)
        const rotateX = ((y - centerY) / centerY) * -10;
        const rotateY = ((x - centerX) / centerX) * 10;

        // Calculate shine position
        const shineX = (x / rect.width) * 100;
        const shineY = (y / rect.height) * 100;

        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;

        const shine = card.querySelector('.card-shine') as HTMLElement;
        if (shine) {
            shine.style.background = `radial-gradient(circle at ${shineX}% ${shineY}%, rgba(255,255,255,0.4) 0%, transparent 60%)`;
            shine.style.opacity = '1';
        }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';

        const shine = card.querySelector('.card-shine') as HTMLElement;
        if (shine) {
            shine.style.opacity = '0';
        }
    };

    const displayRazorpay = async () => {
        if (finalPrice === 0) {
            // Bypass payment for free orders
            const mockPaymentId = 'free_order_' + Math.random().toString(36).substr(2, 9);

            // Send email
            try {
                await sendOrderEmail({
                    full_name: `${formData.firstName} ${formData.lastName}`,
                    email: email,
                    phone: formData.phone,
                    plan_name: plan.name,
                    amount_paid: 0,
                    payment_id: mockPaymentId,
                    address: `Zip: ${formData.zipCode}`,
                    dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`
                });
            } catch (emailError) {
                console.error('Failed to send order emails:', emailError);
            }

            const memberData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
                address: `Zip: ${formData.zipCode}`,
                zipCode: formData.zipCode,
                amountPaid: 0 // Free order
            };

            onSubmit(mockPaymentId, memberData);
            return;
        }

        const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');

        if (!res) {
            alert('Razorpay SDK failed to load. Are you online?');
            return;
        }

        const key = import.meta.env.VITE_RAZORPAY_KEY_ID;

        if (!key || key === 'YOUR_KEY_HERE') {
            alert('Payment configuration missing. Please check VITE_RAZORPAY_KEY_ID in .env');
            return;
        }

        try {
            const isSubscription = plan.id === 'complete' && !isInternational;

            let razorpayOptions: any = {};

            if (isSubscription) {
                // --- SUBSCRIPTION FLOW (Ultimate plan) ---
                const subUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription`;
                const planId = import.meta.env.VITE_RAZORPAY_PLAN_ID;

                if (!planId || planId === 'plan_REPLACE_WITH_YOUR_PLAN_ID') {
                    alert('Subscription plan not configured. Please set VITE_RAZORPAY_PLAN_ID in .env');
                    return;
                }

                const subData = {
                    plan_id: planId,
                    total_count: 12, // 12 months
                    customer_notify: 1,
                    first_charge_amount: finalPrice, // Total shown on page (e.g., 3999)
                    notes: {
                        plan_name: plan.name,
                        user_email: email,
                        full_name: `${formData.firstName} ${formData.lastName}`,
                    }
                };

                const response = await fetch(subUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify(subData)
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('Subscription creation failed:', errorData);
                    alert('Could not initiate subscription. Please try again.');
                    return;
                }

                const subscription = await response.json();

                razorpayOptions = {
                    key: key,
                    subscription_id: subscription.id,
                    name: 'Nostavia Health',
                    description: `${plan.name} Membership — ₹${(INDIA_CONFIG.pricing.ultimateRecurring || 1999).toLocaleString('en-IN')}/month`,
                    image: 'https://nostavia.health/wp-content/uploads/2023/10/Nostavia-Logo-1.png',
                    handler: async function (response: any) {
                        try {
                            await sendOrderEmail({
                                full_name: `${formData.firstName} ${formData.lastName}`,
                                email: email,
                                phone: formData.phone,
                                plan_name: `${plan.name} (Subscription)`,
                                amount_paid: INDIA_CONFIG.pricing.ultimateRecurring || 1999,
                                payment_id: response.razorpay_payment_id,
                                address: `Zip: ${formData.zipCode}`,
                                dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`
                            });
                        } catch (emailError) {
                            console.error('Failed to send order emails:', emailError);
                        }

                        const memberData = {
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            phone: formData.phone,
                            dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
                            address: `Zip: ${formData.zipCode}`,
                            zipCode: formData.zipCode,
                            amountPaid: INDIA_CONFIG.pricing.ultimateRecurring || 1999
                        };

                        onSubmit(response.razorpay_payment_id, memberData);
                    },
                    prefill: {
                        name: `${formData.firstName} ${formData.lastName}`,
                        email: email,
                        contact: formData.phone,
                    },
                    notes: {
                        plan_name: plan.name,
                        full_name: `${formData.firstName} ${formData.lastName}`,
                        email: email,
                        dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
                        zip_code: formData.zipCode,
                        type: 'subscription',
                    },
                    theme: {
                        color: '#000000',
                    },
                };
            } else {
                // --- ONE-TIME ORDER FLOW (Baseline / Pro) ---
                const orderUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-order`;
                const orderData = {
                    amount: finalPrice * 100, // Amount in lowest denomination
                    currency: isInternational ? 'USD' : 'INR',
                    receipt: `receipt_${Date.now()}`,
                    notes: {
                        plan_name: plan.name,
                        user_email: email
                    }
                };

                const response = await fetch(orderUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify(orderData)
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('Order creation failed:', errorData);
                    alert('Could not initiate secured payment. Please try again.');
                    return;
                }

                const order = await response.json();

                razorpayOptions = {
                    key: key,
                    amount: order.amount,
                    currency: order.currency,
                    name: 'Nostavia Health',
                    description: `${plan.name} Membership`,
                    image: 'https://nostavia.health/wp-content/uploads/2023/10/Nostavia-Logo-1.png',
                    order_id: order.id,
                    handler: async function (response: any) {
                        try {
                            await sendOrderEmail({
                                full_name: `${formData.firstName} ${formData.lastName}`,
                                email: email,
                                phone: formData.phone,
                                plan_name: `${plan.name} (x${quantity})`,
                                amount_paid: finalPrice,
                                payment_id: response.razorpay_payment_id,
                                address: `Zip: ${formData.zipCode}`,
                                dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`
                            });
                        } catch (emailError) {
                            console.error('Failed to send order emails:', emailError);
                        }

                        const memberData = {
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            phone: formData.phone,
                            dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
                            address: `Zip: ${formData.zipCode}`,
                            zipCode: formData.zipCode,
                            amountPaid: finalPrice
                        };

                        onSubmit(response.razorpay_payment_id, memberData);
                    },
                    prefill: {
                        name: `${formData.firstName} ${formData.lastName}`,
                        email: email,
                        contact: formData.phone,
                    },
                    notes: {
                        plan_name: plan.name,
                        full_name: `${formData.firstName} ${formData.lastName}`,
                        email: email,
                        dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
                        zip_code: formData.zipCode,
                        address: `Zip: ${formData.zipCode}`,
                        marketing_source: formData.agreeMarketing ? 'Yes' : 'No',
                        promo_code: promoCode || 'None',
                        discount_amount: discount
                    },
                    theme: {
                        color: '#000000',
                    },
                };
            }

            const paymentObject = new window.Razorpay(razorpayOptions);
            paymentObject.open();

        } catch (err) {
            console.error('Payment Error:', err);
            alert('Something went wrong initializing payment.');
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Basic Validation
        if (!formData.firstName || !formData.lastName) {
            alert('Please enter your full name.');
            return;
        }
        if (!formData.zipCode) {
            alert('Please enter your Zip Code.');
            return;
        }
        if (!formData.dobMonth || !formData.dobDay || !formData.dobYear) {
            alert('Please enter your full Date of Birth.');
            return;
        }
        if (!formData.phone) {
            alert('Please enter your Phone Number.');
            return;
        }
        if (!formData.agreeTerms) {
            alert('You must agree to the Terms of Service and Privacy Policy.');
            return;
        }

        if (finalPrice === 0) {
            // Bypass payment for free orders
            // Logic moved to displayRazorpay for unification, called here
            displayRazorpay();
            return;
        }

        displayRazorpay();
    };

    return (
        <div className="checkout-step slide-in-right">
            {showSnowfall && (
                <div className="snowfall-container">
                    {Array.from({ length: 50 }).map((_, i) => (
                        <span
                            key={i}
                            className="snowflake"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 5}s`,
                            }}
                        >
                            ❄
                        </span>
                    ))}
                </div>
            )}
            {showConfetti && (
                <div className="snowfall-container">
                    {Array.from({ length: 50 }).map((_, i) => (
                        <span
                            key={i}
                            className="confetti"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 5}s`,
                                backgroundColor: ['#FFD700', '#FF0000', '#00FF00', '#0000FF', '#FF00FF', '#00FFFF'][Math.floor(Math.random() * 6)],
                                width: '10px',
                                height: '10px',
                            }}
                        >
                        </span>
                    ))}
                </div>
            )}
            <div className="checkout-header">
                <button className="back-btn" onClick={onBack}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>
                <Logo size="medium" />
                <div className="header-spacer"></div>
            </div>

            <div className="checkout-content">
                <div className="checkout-form-section">
                    <div className="form-header">
                        <h1>Join Nostavia</h1>
                        <p className="form-subtitle">
                            {plan.id === 'complete'
                                ? 'Renews Monthly. Cancel anytime.'
                                : 'One-time payment.'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="checkout-form">
                        <div className="input-group">
                            <label className="input-label">Email</label>
                            <input
                                type="email"
                                className="input"
                                value={email}
                                readOnly
                                disabled
                            />
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label className="input-label">First Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="First Name"
                                    value={formData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Last Name</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="Last Name"
                                    value={formData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label className="input-label">Zip Code</label>
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="Your Zip Code"
                                    value={formData.zipCode}
                                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Date of Birth</label>
                                <div className="dob-inputs">
                                    <input
                                        type="text"
                                        className="input dob-input"
                                        placeholder="MM"
                                        maxLength={2}
                                        value={formData.dobMonth}
                                        onChange={(e) => handleInputChange('dobMonth', e.target.value)}
                                    />
                                    <span className="dob-separator">/</span>
                                    <input
                                        type="text"
                                        className="input dob-input"
                                        placeholder="DD"
                                        maxLength={2}
                                        value={formData.dobDay}
                                        onChange={(e) => handleInputChange('dobDay', e.target.value)}
                                    />
                                    <span className="dob-separator">/</span>
                                    <input
                                        type="text"
                                        className="input dob-input dob-year"
                                        placeholder="YYYY"
                                        maxLength={4}
                                        value={formData.dobYear}
                                        onChange={(e) => handleInputChange('dobYear', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="input-group">
                            <label className="input-label">Phone</label>
                            <input
                                type="tel"
                                className="input"
                                placeholder="Your phone number"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                            />
                        </div>

                        <div className="input-group">
                            <label className="input-label">Country</label>
                            <select
                                className="input"
                                value={formData.country}
                                onChange={(e) => {
                                    handleInputChange('country', e.target.value);
                                    if (onCountryChange) {
                                        onCountryChange(e.target.value);
                                    }
                                }}
                            >
                                {COUNTRIES.map(country => (
                                    <option key={country.code} value={country.code}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Blood Panel Selection - Only for India */}
                        {!isInternational && (
                            <div className="blood-panel-section">
                                <h2 className="section-title">Blood Panel</h2>

                                <div
                                    className={`panel-option ${panelOption === 'r1' ? 'selected' : ''}`}
                                    onClick={() => handlePanelOptionChange('r1')}
                                >
                                    <div className="panel-option-image">
                                        <img src="/blood-panel-r1.png" onError={(e) => e.currentTarget.src = '/blood-panel.png'} alt="R1 Panel" className="panel-image" />
                                    </div>
                                    <div className="panel-option-content">
                                        <div className="panel-option-header">
                                            <span className="panel-option-title">Nostavia R1 Panel</span>
                                        </div>
                                        <div className="pricing-row compact">
                                            <span className="price-current">₹999</span>
                                            <span className="price-original">₹1499</span>
                                        </div>
                                        <p className="panel-option-desc">
                                            A foundational health screening designed to detect early metabolic and organ-level imbalances.
                                        </p>
                                        <div className="panel-link-wrapper">
                                            <button
                                                type="button"
                                                className="panel-expand-btn"
                                                onClick={(e) => togglePanelExpansion('r1', e)}
                                            >
                                                {expandedPanel === 'r1' ? 'Hide Included Markers' : 'View Included Markers'}
                                            </button>
                                        </div>
                                        {expandedPanel === 'r1' && (
                                            <div className="panel-markers-container" onClick={(e) => e.stopPropagation()}>
                                                {r1Markers.map((category, idx) => (
                                                    <div key={idx} className="marker-category">
                                                        <h4 className="marker-category-title">{category.category}</h4>
                                                        {category.description && <p className="marker-category-desc">{category.description}</p>}
                                                        <div className="marker-items">
                                                            {category.items.map((item, i) => (
                                                                <span key={i} className="marker-tag">{item}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div
                                    className={`panel-option ${activePromoCode === 'X1' || panelOption === 'x1' ? 'selected' : ''}`}
                                    onClick={() => handlePanelOptionChange('x1')}
                                >
                                    <div className="panel-option-image">
                                        <img src="/blood-panel-x1.png" onError={(e) => e.currentTarget.src = '/blood-panel.png'} alt="X1 Panel" className="panel-image" />
                                    </div>
                                    <div className="panel-option-content">
                                        <div className="panel-option-header">
                                            <span className="panel-option-title">Nostavia X1 Panel</span>
                                            <span className="panel-badge recommended">RECOMMENDED</span>
                                        </div>
                                        <div className="pricing-row compact">
                                            <span className="price-current">₹2,999</span>
                                            <span className="price-original">₹3,999</span>
                                        </div>
                                        <p className="panel-option-desc">
                                            A comprehensive biological assessment built to detect subtle dysfunction before clinical diagnosis.
                                        </p>
                                        <div className="panel-link-wrapper">
                                            <button
                                                type="button"
                                                className="panel-expand-btn"
                                                onClick={(e) => togglePanelExpansion('x1', e)}
                                            >
                                                {expandedPanel === 'x1' ? 'Hide Included Markers' : 'View Included Markers'}
                                            </button>
                                        </div>
                                        {expandedPanel === 'x1' && (
                                            <div className="panel-markers-container" onClick={(e) => e.stopPropagation()}>
                                                {x1Markers.map((category, idx) => (
                                                    <div key={idx} className="marker-category">
                                                        <h4 className="marker-category-title">{category.category}</h4>
                                                        <div className="marker-items">
                                                            {category.items.map((item, i) => (
                                                                <span key={i} className="marker-tag">{item}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div
                                    className={`panel-option own-report-panel ${panelOption === 'own' ? 'selected' : ''}`}
                                    onClick={() => handlePanelOptionChange('own')}
                                >
                                    <div className="panel-option-content">
                                        <div className="panel-option-header">
                                            <span className="panel-option-title">Use My Own Lab Report</span>
                                            {labReportApproved && <span className="panel-badge approved" style={{ marginLeft: '4px' }}>+ VERIFIED DISCOUNT</span>}
                                        </div>
                                        <p className="panel-option-desc">
                                            Got a recent lab report? Verify it to get ₹{plan.id === 'baseline' ? '700' : '2,500'} off
                                        </p>
                                    </div>
                                </div>

                                {/* Lab Verification Form */}
                                {showLabVerification && (
                                    <div className="lab-verification-form">
                                        <div className="lab-input-group">
                                            <label className="lab-input-label">Report Date</label>
                                            <input
                                                type="date"
                                                className="lab-input"
                                                value={reportDate}
                                                onChange={(e) => setReportDate(e.target.value)}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>

                                        <div className="lab-input-group">
                                            <label className="lab-input-label">Number of Parameters</label>
                                            <input
                                                type="number"
                                                className="lab-input"
                                                placeholder="e.g., 75"
                                                value={parameterCount}
                                                onChange={(e) => setParameterCount(e.target.value)}
                                                min="1"
                                            />
                                        </div>

                                        <div className="lab-domains-group">
                                            <label className="lab-input-label">Domains Covered</label>
                                            <p className="lab-domains-hint">Select all health domains your report covers</p>
                                            <div className="domain-grid">
                                                {healthDomains.map(domain => (
                                                    <label
                                                        key={domain.id}
                                                        className={`domain-checkbox ${selectedDomains.includes(domain.id) ? 'checked' : ''} ${domain.required ? 'required' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDomains.includes(domain.id)}
                                                            onChange={() => handleDomainToggle(domain.id)}
                                                        />
                                                        <span className="domain-name">
                                                            {domain.name}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="lab-verification-actions">
                                            {labReportApproved ? (
                                                <button
                                                    type="button"
                                                    className="verify-btn reset"
                                                    onClick={handleResetLabVerification}
                                                >
                                                    Reset
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="verify-btn"
                                                    onClick={handleVerifyLabReport}
                                                    disabled={!reportDate || !parameterCount}
                                                >
                                                    Verify Report
                                                </button>
                                            )}
                                        </div>

                                        {labVerificationMessage && (
                                            <p className={`lab-verification-message ${labVerificationMessage.type}`}>
                                                {labVerificationMessage.text}
                                            </p>
                                        )}

                                        <p className="lab-recommendation">
                                            For the most comprehensive analysis and best results, we recommend using Nostavia's specially designed blood panel.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Add-Ons Section - Only for India */}
                        {!isInternational && (
                            <div className="addons-section">
                                <h2 className="section-title">Add-Ons</h2>
                                <p className="addons-subtitle">Enhance your health journey with premium tests</p>

                                <div className="addons-grid">
                                    {addOns.map(addon => (
                                        <div
                                            key={addon.id}
                                            className={`addon-card ${selectedAddOns.includes(addon.id) ? 'selected' : ''}`}
                                            onClick={() => handleAddOnToggle(addon.id)}
                                        >
                                            {addon.image && (
                                                <div className="addon-image-wrapper">
                                                    <img
                                                        src={addon.image}
                                                        alt={addon.name}
                                                        className="addon-image"
                                                    />
                                                </div>
                                            )}
                                            <div className="addon-content">
                                                <h4 className="addon-title">{addon.name}</h4>
                                                <span className="addon-price">₹{addon.price.toLocaleString('en-IN')}</span>
                                                <p className="addon-text">{addon.description}</p>
                                                {addon.poweredBy && (
                                                    <span className="addon-source">Powered by {addon.poweredBy}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="checkboxes-section">
                            <div className="checkbox-group">
                                <input
                                    type="checkbox"
                                    id="agreeTerms"
                                    className="checkbox"
                                    checked={formData.agreeTerms}
                                    onChange={(e) => handleInputChange('agreeTerms', e.target.checked)}
                                />
                                <label htmlFor="agreeTerms" className="checkbox-label">
                                    By checking this box and confirming below, I acknowledge that I have read, understand, and agree to Nostavia's{' '}
                                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>, <a href="/consent" target="_blank" rel="noopener noreferrer">Informed Medical Consent</a>,{' '}
                                    <a href="/agreement" target="_blank" rel="noopener noreferrer">Membership Agreement</a>, <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                                    <a href="/practices" target="_blank" rel="noopener noreferrer">Notice of Medical Group Privacy Practices</a>
                                </label>
                            </div>

                            <div className="checkbox-group">
                                <input
                                    type="checkbox"
                                    id="agreeMarketing"
                                    className="checkbox"
                                    checked={formData.agreeMarketing}
                                    onChange={(e) => handleInputChange('agreeMarketing', e.target.checked)}
                                />
                                <label htmlFor="agreeMarketing" className="checkbox-label">
                                    I agree to receive personalized offers & reminders based on my Action Plan & Lab results. I consent to Nostavia using my health data for this. Opt out anytime.
                                </label>
                            </div>
                        </div>

                        <div className="payment-section">
                            <h2 className="section-title">Payment</h2>
                            <p className="payment-note">
                                By subscribing, you authorize Nostavia to charge you according to the terms of the membership agreement.
                            </p>
                        </div>

                        {/* Mobile-only order summary at bottom */}
                        <div className="mobile-order-summary">
                            <div className="mobile-summary-row">
                                <span className="mobile-summary-label">{plan.name}</span>
                                <span className="mobile-summary-value">{currencySymbol}{isInternational ? displayPrice : (plan.price * quantity).toLocaleString('en-IN')}</span>
                            </div>

                            {!isInternational && selectedAddOns.length > 0 && addOns.filter(a => selectedAddOns.includes(a.id)).map(addon => (
                                <div key={addon.id} className="mobile-summary-row addon-row">
                                    <span className="mobile-summary-label">{addon.name}</span>
                                    <span className="mobile-summary-value">+₹{addon.price.toLocaleString('en-IN')}</span>
                                </div>
                            ))}

                            {!isInternational && totalDiscount > 0 && (
                                <div className="mobile-summary-row discount-row">
                                    <span className="mobile-summary-label">Discount</span>
                                    <span className="mobile-summary-value discount">-₹{totalDiscount.toLocaleString('en-IN')}</span>
                                </div>
                            )}

                            <div className="mobile-summary-divider"></div>

                            <div className="mobile-summary-row total-row">
                                <span className="mobile-total-label">Total</span>
                                <span className="mobile-total-amount">{currencySymbol}{isInternational ? finalPrice : finalPrice.toLocaleString('en-IN')}</span>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary btn-full submit-btn">
                            Complete Purchase
                        </button>
                    </form>
                </div>

                <div className="order-summary-section">
                    <div className="order-summary-card card">
                        <h2 className="summary-title">Order Summary</h2>

                        <div className="plan-preview">
                            <div
                                className="plan-preview-img-wrapper"
                                onMouseMove={handleMouseMove}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div className="card-shine"></div>
                                <img
                                    src={isInternational
                                        ? (plan.id === 'baseline' ? '/card-baseline-usd.png' : plan.id === 'performance' ? '/card-pro-usd.png' : '/card-ultimate-usd.png')
                                        : (plan.id === 'baseline' ? '/card-baseline.png' : plan.id === 'performance' ? '/card-pro.png' : '/card-ultimate.png')}
                                    alt={plan.name}
                                    className="plan-preview-img"
                                />
                            </div>
                        </div>

                        <div className="summary-plan-info">
                            <h3 className="summary-plan-name">{plan.name}</h3>
                            <p className="summary-plan-desc">
                                For <span className="highlight">{plan.targetAudience}</span>{' '}
                                {plan.description.replace(`For ${plan.targetAudience} `, '')}
                            </p>
                            <p className="summary-price">
                                <span className="price-currency">{currencySymbol}</span>
                                <span className="price-amount">
                                    {isInternational ? displayPrice : (plan.id === 'complete' && quantity === 1 ? '3,999' : (quantity > 1 ? totalPrice.toLocaleString('en-IN') : plan.price.toLocaleString('en-IN')))}
                                </span>
                                <span className="price-period">
                                    {plan.id === 'complete' ? (isInternational ? '/month' : 'first month') : 'one-time'}
                                </span>
                            </p>
                            {plan.id === 'complete' && !isInternational && (
                                <p className="price-note" style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '-8px', marginBottom: '16px' }}>
                                    Then ₹2,499/month
                                </p>
                            )}
                            <button type="button" className="change-tier-btn" onClick={onChangeTier}>
                                Change Tier
                            </button>
                        </div>

                        <div className="summary-features">
                            <p className="summary-features-label">
                                Includes:
                            </p>
                            <ul className="summary-features-list">
                                {plan.mainFeatures
                                    .filter(feature => !isInternational || !feature.text.includes('100+ labs'))
                                    .map((feature, i) => (
                                        <li key={i} className="summary-feature-item">
                                            <span className="summary-feature-icon">{getIcon(feature.icon)}</span>
                                            <div className="summary-feature-text">
                                                <span className="summary-feature-title">{feature.text}</span>
                                                {feature.subtext && <span className="summary-feature-subtext">{feature.subtext}</span>}
                                            </div>
                                        </li>
                                    ))}
                            </ul>

                            {(plan.id === 'performance' || plan.id === 'complete') && (
                                <>
                                    <p className="summary-features-label still-includes">Still includes:</p>
                                    <ul className="summary-features-list small">
                                        {plan.includedFeatures.map((feature, i) => (
                                            <li key={i} className="summary-feature-item small">
                                                <span className="summary-feature-icon">{getIcon(feature.icon)}</span>
                                                <span className="summary-feature-title">{feature.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>


                        <div className="promo-section">
                            <div className="promo-input-group">
                                <input
                                    type="text"
                                    className="promo-input"
                                    placeholder="Enter promo code"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value)}
                                    disabled={discount > 0}
                                    // Allow submitting with Enter key
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (discount > 0) return;
                                            handleApplyPromo();
                                        }
                                    }}
                                />
                                {discount > 0 ? (
                                    <button
                                        type="button"
                                        className="apply-btn remove-btn"
                                        onClick={handleRemovePromo}
                                        style={{ backgroundColor: '#ef4444', color: 'white' }}
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="apply-btn"
                                        onClick={handleApplyPromo}
                                        disabled={!promoCode.trim()}
                                    >
                                        Apply
                                    </button>
                                )}
                            </div>
                            {promoMessage && (
                                <p className={`promo-message ${promoMessage.type}`}>
                                    {promoMessage.text}
                                </p>
                            )}
                        </div>

                        {discount > 0 && (
                            <div className="discount-row">
                                <span>Promo Discount</span>
                                <span>-{currencySymbol}{isInternational ? discount : discount.toLocaleString('en-IN')}</span>
                            </div>
                        )}

                        {panelDiscount > 0 && (
                            <div className="discount-row">
                                <span>R1 Panel Adjustment</span>
                                <span>-₹{panelDiscount.toLocaleString('en-IN')}</span>
                            </div>
                        )}

                        {panelUpcharge > 0 && (
                            <div className="discount-row" style={{ color: 'var(--color-gray-700)' }}>
                                <span>X1 Panel Upgrade</span>
                                <span>+₹{panelUpcharge.toLocaleString('en-IN')}</span>
                            </div>
                        )}

                        {labReportDiscount > 0 && (
                            <div className="discount-row lab-discount">
                                <span>Lab Report Discount</span>
                                <span>-₹{labReportDiscount.toLocaleString('en-IN')}</span>
                            </div>
                        )}

                        {selectedAddOns.length > 0 && (
                            <div className="addons-summary">
                                {addOns
                                    .filter(addon => selectedAddOns.includes(addon.id))
                                    .map(addon => (
                                        <div key={addon.id} className="addon-summary-row">
                                            <span className="addon-summary-name">{addon.name}</span>
                                            <span className="addon-summary-price">+₹{addon.price.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        )}

                        <div className="summary-total">
                            <span className="total-label">Total</span>
                            <span className="total-amount">{currencySymbol}{isInternational ? finalPrice : (plan.id === 'complete' && totalDiscount === 0 && addOnsTotal === 0 ? '3,999' : finalPrice.toLocaleString('en-IN'))}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
}
